import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Element } from '@svgdotjs/svg.js';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { cloneDeep } from 'lodash';
import { Device } from '../../../../../model/device';
import { DevicesService } from '../../../../../services/devices.service';
import { Commands, CommandsSchema, CommandsSchemas, Params } from '@iotsens/commands';
import { SynopticFile } from '../../../../../model/synoptics/synoptic-file';
import {
  SynopticConfigurationComponent,
  SynopticElementAction,
  SynopticElementEvent
} from '../../../../../model/synoptics/synoptic-configuration-types';
import {
  ExecuteCommandArgs, LinkToSynopticArgs,
  ShowPopupArgs,
  ShowTooltipArgs,
  TooltipRow
} from '../../../../../model/synoptics/synoptic-arg-types';
import { DeviceMeasurement } from '../../../../../model/device-measurement';
import { AssetMeasurement } from '../../../../../model/asset-measurement';
import { Kpi } from '../../../../../model/kpi';
import {
  SynopticVisualizationCommandParamsComponent
} from '../../../synoptic-details/synoptic-visualization/synoptic-visualization-command-params/synoptic-visualization-command-params.component';
import { translate } from '@ngneat/transloco';
import { take } from 'rxjs/operators';
import { DialogService } from 'primeng/dynamicdialog';
import {
  SynopticsFormEventActionComponent
} from './synoptics-form-event-action/synoptics-form-event-action.component';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'app-synoptics-form-events',
  templateUrl: './synoptics-form-events.component.html',
  styleUrls: ['./synoptics-form-events.component.scss']
})
export class SynopticsFormEventsComponent implements OnInit {

  @Input() selection: Element;
  @Input() events: SynopticElementEvent[];
  @Input() synopticInfoForm: FormGroup;
  @Input() deviceMeasurements: DeviceMeasurement[];
  @Input() assetMeasurements: AssetMeasurement[];
  @Input() kpis: Kpi[];
  @Input() devices: Device[];
  @Input() uuidDeviceMap: Map<string, string>;
  @Input() uuidSensorMap: Map<string, string>;
  @Input() components: SynopticConfigurationComponent[];
  @Input() synopticFiles: SynopticFile[];
  @Input() synopticUuid?: string;
  @Output() eventsChange: EventEmitter<SynopticElementEvent[]>;
  @Output() changeSelectionById: EventEmitter<string>;

  public editActionDialogVisible: boolean;
  public triggerOptions: SelectItem[];
  public actionOptionsClick: SelectItem[];
  public actionOptionsHover: SelectItem[];

  public commandsSchemaMap: Map<string, CommandsSchemas>;

  constructor(private dialogService: DialogService, private devicesService: DevicesService) {
    this.eventsChange = new EventEmitter<SynopticElementEvent[]>();
    this.changeSelectionById = new EventEmitter<string>();
    this.editActionDialogVisible = false;
    this.triggerOptions = [
      { label: 'misc.synopticTriggerTypes.click', value: 'click' },
      { label: 'misc.synopticTriggerTypes.hover', value: 'hover' }
    ];
    this.actionOptionsHover = [
      { label: 'misc.synopticActionTypes.showTooltip', value: 'showTooltip' },
    ];
    this.commandsSchemaMap = new Map<string, CommandsSchemas>();
  }

  @Input()
  set currentComponent(currentComponent: SynopticConfigurationComponent) {
    this.actionOptionsClick = [
      { label: 'misc.synopticActionTypes.executeCommand', value: 'executeCommand' },
      { label: 'misc.synopticActionTypes.showPopup', value: 'showPopup' },
      { label: 'misc.synopticActionTypes.linkToSynoptic', value: 'linkToSynoptic' },
    ];
    if (!('children' in currentComponent)) {
      this.actionOptionsClick.push({ label: 'misc.synopticActionTypes.closePopup', value: 'closePopup' });
    }
  }

  ngOnInit(): void {
  }

  public addNewEvent(): void {
    const event = this.generateDefaultEvent(this.selection.id());
    this.events = [...this.events, event];
    this.onEventsChange();
  }

  public duplicateEvent(event: SynopticElementEvent): void {
    const duplicated = cloneDeep(event);
    duplicated.id = this.selection?.id() ?? duplicated.id;
    this.events.push(duplicated);
    this.onEventsChange();
  }

  public removeEvent(event: SynopticElementEvent): void {
    this.events = this.events.filter(e => e != event);
    this.onEventsChange();
  }

  public startEditingAction(event: SynopticElementEvent): void {
    const data = {
      action: event.action,
      synopticFiles: this.synopticFiles,
      synopticInfoForm: this.synopticInfoForm,
      deviceMeasurements: this.deviceMeasurements,
      assetMeasurements: this.assetMeasurements,
      kpis: this.kpis,
      devices: this.devices,
      uuidDeviceMap: this.uuidDeviceMap,
      uuidSensorMap: this.uuidSensorMap,
      commandsSchemaMap: this.commandsSchemaMap,
      components: this.components,
      synopticUuid: this.synopticUuid
    };

    const ref = this.dialogService.open(SynopticsFormEventActionComponent, {
      header: translate(event.action?.type ? ('misc.synopticActionTypes.' + event.action.type) : 'edit.configuration'),
      style: { width: 'min(700px, 100vw)', maxHeight: '75vh' },
      modal: true,
      data: data
    });

    ref.onClose.pipe(take(1)).subscribe((newAction: SynopticElementAction) => {
      if (newAction) {
        event.action = newAction;
        this.onEventsChange();
      }
    });
  }

  public onEventsChange() {
    this.eventsChange.emit(this.events);
  }

  public generateDefaultArgsForAction(actionType: string): any {
    switch (actionType) {
      case 'showPopup':
        return { component: null } as ShowPopupArgs;
      case 'executeCommand':
        return { deviceUuid: null, schema: null, command: null, params: null, askParamsToUser: false } as ExecuteCommandArgs;
      case 'closePopup':
        return {};
      case 'showTooltip':
        return { rows: [], showDates: false } as ShowTooltipArgs;
      case 'linkToSynoptic':
        return { synopticUuid: null } as LinkToSynopticArgs;
    }
  }

  private generateDefaultEvent(elementId: string): SynopticElementEvent {
    return { id: elementId, trigger: 'click', action: { type: null, args: {} } };
  }

}
